/**
 * A tiny History-API router (no dependency): location subscription, pattern matching and
 * programmatic navigation. Components (`Link`, `Redirect`) live in `Link.tsx`.
 *
 * Demo mode: every URL under `/demo` runs the same routes against offline fixtures. The router
 * strips the `/demo` base from the path it hands to screens and adds it back when navigating, so
 * screens never need to know which mode they run in.
 */
import { useMemo, useSyncExternalStore } from 'react';

export const DEMO_BASE = '/demo';
const NAV_EVENT = 'wb:navigate';

export function currentBase(pathname = location.pathname): string {
  return pathname === DEMO_BASE || pathname.startsWith(`${DEMO_BASE}/`) ? DEMO_BASE : '';
}

export function isDemoPath(pathname = location.pathname): boolean {
  return currentBase(pathname) === DEMO_BASE;
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener('popstate', onChange);
  window.addEventListener(NAV_EVENT, onChange);
  return () => {
    window.removeEventListener('popstate', onChange);
    window.removeEventListener(NAV_EVENT, onChange);
  };
}

const snapshot = () => `${location.pathname}${location.search}`;

export interface AppLocation {
  /** Path without the demo base, e.g. "/m/AB12CD/map". */
  path: string;
  /** Raw search string including "?" (or ""). */
  search: string;
  query: URLSearchParams;
  base: string;
}

export function parseLocation(href: string): AppLocation {
  const url = new URL(href, 'http://local');
  const base = currentBase(url.pathname);
  const stripped = url.pathname.slice(base.length) || '/';
  const path = stripped.length > 1 && stripped.endsWith('/') ? stripped.slice(0, -1) : stripped;
  return { path, search: url.search, query: url.searchParams, base };
}

export function useLocation(): AppLocation {
  const href = useSyncExternalStore(subscribe, snapshot, () => '/');
  return useMemo(() => parseLocation(href), [href]);
}

/** Resolve an app path (or "?query" on the current path) to a full URL path with the base. */
export function resolveHref(to: string): string {
  const base = currentBase();
  if (to.startsWith('?') || to === '') {
    return `${location.pathname}${to}`;
  }
  if (to.startsWith(DEMO_BASE)) return to;
  return `${base}${to === '/' && base ? '' : to}` || '/';
}

interface NavigateOptions {
  replace?: boolean;
}

function historyIndex(): number {
  const state: unknown = history.state;
  return typeof state === 'object' && state !== null && 'wbIdx' in state && typeof state.wbIdx === 'number'
    ? state.wbIdx
    : 0;
}

export function navigate(to: string, options: NavigateOptions = {}): void {
  const href = resolveHref(to);
  if (href === snapshot()) return;
  if (options.replace) history.replaceState({ wbIdx: historyIndex() }, '', href);
  else history.pushState({ wbIdx: historyIndex() + 1 }, '', href);
  window.dispatchEvent(new Event(NAV_EVENT));
}

/** Go back within the app if we can, otherwise replace with `fallback` (deep-link entry). */
export function goBack(fallback: string): void {
  if (historyIndex() > 0) history.back();
  else navigate(fallback, { replace: true });
}

/** Update query parameters on the current path (replace by default). */
export function setQuery(patch: Record<string, string | null>, options: NavigateOptions = { replace: true }) {
  const params = new URLSearchParams(location.search);
  for (const [key, value] of Object.entries(patch)) {
    if (value === null) params.delete(key);
    else params.set(key, value);
  }
  const qs = params.toString();
  navigate(`${parseLocation(location.href).path}${qs ? `?${qs}` : ''}`, options);
}

export type RouteParams = Record<string, string>;

/** Match "/m/:code/armies/:armyId" against a path. Returns params or null. */
export function matchPath(pattern: string, path: string): RouteParams | null {
  const p = pattern.split('/').filter(Boolean);
  const s = path.split('/').filter(Boolean);
  if (p.length !== s.length) return null;
  const params: RouteParams = {};
  for (let i = 0; i < p.length; i++) {
    const part = p[i] ?? '';
    const seg = s[i] ?? '';
    if (part.startsWith(':')) params[part.slice(1)] = decodeURIComponent(seg);
    else if (part !== seg) return null;
  }
  return params;
}
